import { useContext, useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-csp';

import { getScrapedComps } from './db';
import { AppContext } from './AppContext';


const clusterCircles = () => {
  return {
      id: 'scraped-clusters',
      type: 'circle',
      source: 'scraped-comps',
      filter: ['has', 'point_count'],
      paint: {
          // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
          // with three steps to implement three types of circles:
          //   * Blue, 20px circles when point count is less than 100
          //   * Yellow, 30px circles when point count is between 100 and 750
          //   * Pink, 40px circles when point count is greater than or equal to 750
          'circle-color': [
              'step',
              ['get', 'point_count'],
              '#51bbd6',
              100,
              '#f1f075',
              750,
              '#f28cb1'
          ],
          'circle-radius': [
              'step',
              ['get', 'point_count'],
              20,
              100,
              30,
              750,
              40
          ]
      }
  }
}


const clusterCount = () => {
  return {
      id: 'scraped-cluster-count',
      type: 'symbol',
      source: 'scraped-comps',
      filter: ['has', 'point_count'],
      layout: {
          'text-field': ['format', '$', ['to-string', ['/', ['get', '$/ac SUM'], ['get', 'point_count']]], ' / ac'],
          // 'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
      }
  }
}


const unclusteredPoint = () => {
  return {
      id: 'scraped-unclustered-point',
      type: 'circle',
      source: 'scraped-comps',
      filter: ['!', ['has', 'point_count']],
      paint: {
          'circle-color': '#11b4da',
          'circle-radius': 4,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff'
      }
  }
}


const unclusteredPointCount = () => {
  return {
      id: 'scraped-unclustered-point-text',
      type: 'symbol',
      source: 'scraped-comps',
      filter: ['has', '$/ac'],
      layout: {
          /* eslint-disable no-template-curly-in-string */
          'text-field': '${$/ac} / ac',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
      }
  }
}


function onCluster (e) {
    const map = e.target;
    var features = map.queryRenderedFeatures(e.point, {
        layers: ['scraped-clusters']
    });
    var clusterId = features[0].properties.cluster_id;
    map.getSource('scraped-comps').getClusterExpansionZoom(
        clusterId,
        function (err, zoom) {
            if (err) return;

            map.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom
            });
        }
    );
}


function onClusterPoint(e) {
  const map = e.target;
  var coordinates = e.features[0].geometry.coordinates.slice();

  // Ensure that if the map is zoomed out such that
  // multiple copies of the feature are visible, the
  // popup appears over the copy being pointed to.
  while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
  }

  new mapboxgl.Popup()
      .setLngLat(coordinates)
      .setHTML(
        Object.entries(e.features[0].properties).map(([k, v]) => {
          return `<div> <span style="font-weight:bold;"> ${k} </span>: ${v} </div>`;
        }).join('')
      )
      .addTo(map);
}

function inPointer(e) {
  const map = e.target;
  map.getCanvas().style.cursor = 'pointer';
}

function outPointer(e) {
  const map = e.target;
  map.getCanvas().style.cursor = '';
}


export const onInteraction = (map) => {
	// inspect a cluster on click
    map.on('click', 'scraped-clusters', onCluster);

    // When a click event occurs on a feature in
    // the unclustered-point layer, open a popup at
    // the location of the feature, with
    // description HTML from its properties.
    map.on('click', 'scraped-unclustered-point', onClusterPoint);

    ['scraped-unclustered-point', 'scraped-clusters'].forEach(l => {
      map.on('mouseenter', l, inPointer);
      map.on('mouseleave', l, outPointer);
    })
}

export const offInteraction = (map) => {
  // inspect a cluster on click
    map.off('click', 'scraped-clusters', onCluster);

    // When a click event occurs on a feature in
    // the scraped-unclustered-point layer, open a popup at
    // the location of the feature, with
    // description HTML from its properties.
    map.off('click', 'scraped-unclustered-point', onClusterPoint);

    ['unclustered-point', 'scraped-clusters'].forEach(l => {
      map.off('mouseenter', l, inPointer);
      map.off('mouseleave', l, outPointer);
    })
}




// {type: 'FeatureCollection', features: []}
export const addSource = (map, allCompsFC) => {
	return map.addSource('scraped-comps', {
	    type: 'geojson',
	    data: allCompsFC.current,
	    cluster: true,
	    clusterMaxZoom: 14, // Max zoom to cluster points on
	    clusterRadius: 50, // Radius of each cluster when clustering points (defaults to 50)
	    clusterProperties: {
	      "$/ac SUM": ["+", ["get", "$/ac"]]
	    }
	});
}


export const updateSource = (user, mapboxRef, allCompsFC) => {
  if (!user) return;
  getScrapedComps(user)
    .then(fc => {
      fc.features.forEach(f => {
        f.properties['$/ac'] = f.properties.price_per_acre
        f.geometry = {type: 'Point', coordinates: [f.properties.gps_lon, f.properties.gps_lat]}
      })
    
      allCompsFC.current = fc;
      if (!mapboxRef.current) return;
      const source = mapboxRef.current.getSource('scraped-comps');
      if (source) source.setData(allCompsFC.current);
    });
}


export const allCompsLayer = [
	clusterCircles(),
	clusterCount(),
	unclusteredPoint(),
	unclusteredPointCount(),
]



export const useScrapedCompsToggle = () => {
  const { user, mapboxRef } = useContext(AppContext);
  const allCompsFC = useRef({type: 'FeatureCollection', features: []}); 
  
  useEffect(() => {
    updateSource(user, mapboxRef, allCompsFC);
  }, [user, mapboxRef, allCompsFC]);

   // preAdd={map => addSource(map, allCompsFC)}
   //  postAdd={onInteraction}

  const compsToggle = (toggle) => {
    if (!mapboxRef.current) return;
    if (!mapboxRef.current.getSource('scraped-comps')) addSource(mapboxRef.current, allCompsFC);

    const layers = allCompsLayer;

    layers.forEach(l => {
      offInteraction(mapboxRef.current);
      if (mapboxRef.current.getLayer(l.id)) mapboxRef.current.removeLayer(l.id);
      if (toggle) {
        mapboxRef.current.addLayer(l);
        onInteraction(mapboxRef.current);
      }
    });
  }

  return compsToggle;
}

